import React from "react";
import { IMedia, IPlaylistResponseDto } from "../../../types";
import * as Styled from "./Playlist.styled";
import { playlistsService } from "../../../services";
import { MediaCollection } from "../../media-collection";
import { ChevronLeft, ChevronRight } from "react-bootstrap-icons";

export const Playlist: React.FC<{
    playlist: IPlaylistResponseDto;
    clientId: string;
    enableRatingControls: boolean;
    sessionToken: string;
    isSignedIn: boolean;
    mediaPerPage: number;
    templateId: number;
    enableCompetitionMode?:boolean;
    enableAutoplay: boolean;
}> = ({
    playlist,
    clientId,
    enableRatingControls,
    sessionToken,
    isSignedIn,
    mediaPerPage,
    templateId,
    enableCompetitionMode,
    enableAutoplay,
}) => {
        const DESCRIPTION_LENGTH = 50;
        const [description, setDescription] = React.useState<string>("");
        const [descriptionTransition, setDescriptionTransition] =
            React.useState<boolean>(false);
        const [isTruncated, setIsTruncated] = React.useState<boolean>(false);
        const [isExpanded, setIsExpanded] = React.useState<boolean>(true);
        const [loadMore, setLoadMore] = React.useState<boolean>(false);
        const [allMediaItems, setAllMediaItems] = React.useState<IMedia[]>([]);
        const [showLoader, setShowLoader] = React.useState<boolean>(false);

        const handleLoadMore = React.useCallback(async () => {
            if (allMediaItems.length === 0) {
                setShowLoader(true);
                const resp = await playlistsService.getPlaylistById(
                    playlist.clientId,
                    playlist.playlistId
                );
                setAllMediaItems(resp.mediaItems);
                setShowLoader(false);
            }

            setLoadMore((prev) => !prev);
        }, [playlist, setAllMediaItems, setLoadMore, allMediaItems]);

        const areMoreItemsAvailable = React.useMemo(() => {
            return playlist.length > playlist.mediaItems.length;
        }, [playlist]);

        React.useEffect(() => {
            if (playlist.description.length > DESCRIPTION_LENGTH) {
                setIsTruncated(true);
                setIsExpanded(false);
                setDescription(
                    playlist.description.substring(0, DESCRIPTION_LENGTH)
                );
            } else {
                setDescription(playlist.description);
            }
        }, [playlist.description, setDescription, setIsTruncated, setIsExpanded]);

        const typeWriter = React.useCallback(
            (text: string, index: number) => {
                if (index < text.length) {
                    setDescription((prev) => prev + text.charAt(index));
                    setTimeout(() => typeWriter(text, index + 1), 1);
                } else {
                    setDescriptionTransition(false);
                }
            },
            [setDescription]
        );

        const reverseTypeWriter = React.useCallback(
            (text: string, index: number) => {
                if (index >= DESCRIPTION_LENGTH) {
                    setDescription(text.substring(0, index));
                    setTimeout(() => reverseTypeWriter(text, index - 1), 1);
                } else {
                    setDescriptionTransition(false);
                }
            },
            [setDescription]
        );

        const handleExpand = React.useCallback(() => {
            setDescriptionTransition(true);
            if (isExpanded) {
                reverseTypeWriter(
                    playlist.description,
                    playlist.description.length
                );
            } else {
                typeWriter(playlist.description, 50);
            }
            setIsExpanded(!isExpanded);
        }, [
            isExpanded,
            setIsExpanded,
            playlist.description,
            typeWriter,
            reverseTypeWriter,
        ]);

        return (
            <>
                <Styled.PlaylistContainer>
                    <Styled.PlaylistTitleWrapper>
                        <Styled.PlaylistTitle>
                            {playlist.title}
                        </Styled.PlaylistTitle>
                        {areMoreItemsAvailable && (
                            <Styled.PlaylistTitleLoadMore onClick={handleLoadMore}>
                                {!loadMore ? (
                                    <>
                                        Load More
                                        <ChevronRight />
                                    </>
                                ) : (
                                    <>
                                        <ChevronLeft />
                                        Load Less
                                    </>
                                )}
                            </Styled.PlaylistTitleLoadMore>
                        )}
                    </Styled.PlaylistTitleWrapper>
                    <Styled.PlaylistDescription>
                        {description}
                        {isTruncated && (
                            <Styled.ExpandButton onClick={handleExpand}>
                                {!descriptionTransition &&
                                    (isExpanded ? "Read Less" : "... Read More")}
                            </Styled.ExpandButton>
                        )}
                    </Styled.PlaylistDescription>

                  
                    <MediaCollection
                     existingMedia={
                            loadMore ? allMediaItems : playlist.mediaItems
                        }
                        clientId={clientId}
                        fetchByDate="false"
                        enableFilterControls={false}
                        enableRatingControls={enableRatingControls}
                        enableShareControls={false}
                        sessionToken={sessionToken}
                        isSignedIn={isSignedIn}
                        mediaPerPage={mediaPerPage}
                        externalLoader={showLoader}
                        enableCompetitionMode={enableCompetitionMode}
                        enableAutoplay={enableAutoplay}
                    />
                </Styled.PlaylistContainer>
            </>
        );
    };
